import { MUIButton, MUIDialog, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect, useState } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { toast } from "react-toastify"
import { z } from "zod"
import { useMacAddresses } from "../../../../hooks/useMacAddresses"
import { digitalManagerApi } from "../../../../services/api"
import { MacAddressGet } from "../../../models/SupplierPortal"
import { booleanComparator, getDoneIcon, getEmptyIcon } from "../../../utils/getBooleanFlagIcon"
import {
  dateStringComparator,
  getNiceFormattedDate,
} from "../../../utils/getFormattedDate"
import { ControllerRendererProps } from "../../../types/reactHookForm"
import { EntityRow, EntityTab } from "../../partials/EntityTab"

function parseMacAddress(macAddress: string) {
  return macAddress.split(":").join("")
}

function isValidRange(highMacAddress: string, lowMacAddress: string) {
  const parsedHighMacAddress = "0x" + parseMacAddress(highMacAddress)
  const parsedLowMacAddress = "0x" + parseMacAddress(lowMacAddress)
  return Number.parseInt(parsedHighMacAddress, 16) > Number.parseInt(parsedLowMacAddress, 16)
}

const MAC_REGEX = /^([0-9A-Fa-f]{2}:){5}([0-9A-Fa-f]{2})$/

export function CreateMacAddressDialog(props: {
  readonly elaborateMacAddress: boolean
  readonly reloadMacAddresses: () => void
  readonly setElaborateMacAddress: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { elaborateMacAddress, reloadMacAddresses, setElaborateMacAddress } = props

  const validationSchema = z.object({
    lowMacAddress: z
      .string()
      .min(1, "Please enter a mac address")
      .regex(MAC_REGEX, "Please enter a valid mac address"),
    highMacAddress: z
      .string()
      .min(1, "Please enter a mac address")
      .regex(MAC_REGEX, "Please enter a valid mac address"),
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<{ lowMacAddress: string; highMacAddress: string }>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({ lowMacAddress: "", highMacAddress: "" })
  }, [reset])

  const onSubmit = useCallback(
    async (values: { lowMacAddress: string; highMacAddress: string }) => {
      const { lowMacAddress, highMacAddress } = values

      if (!isValidRange(highMacAddress, lowMacAddress)) {
        toast.error("High MAC Address must be greater than Low MAC Address.")
        return
      }

      try {
        const response = await digitalManagerApi.post(
          `/litePanelPro/macAddresses`,
          null,
          {
            params: {
              macAddressLow: parseMacAddress(lowMacAddress),
              macAddressHigh: parseMacAddress(highMacAddress),
            },
          }
        )

        if (response.status === StatusCodes.OK) {
          toast.success("MAC addresses created")
          setElaborateMacAddress(false)
          reloadMacAddresses()
        }
      } catch {
        toast.error("Cannot create MAC addresses")
      }
    },
    [reloadMacAddresses, setElaborateMacAddress]
  )

  const handleClose = useCallback(() => {
    setElaborateMacAddress(false)
  }, [setElaborateMacAddress])

  const handleTextChange = useCallback(
    (
      field: ControllerRenderProps<
        { lowMacAddress: string; highMacAddress: string },
        "lowMacAddress" | "highMacAddress"
      >
    ) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const renderTextFieldLowMacAddress = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      { lowMacAddress: string; highMacAddress: string },
      "lowMacAddress"
    >) => (
      <MUITextField
        id="lowMacAddress"
        name={field.name}
        label="Low MAC Address"
        placeholder="1a:2b:3c:4d:5e:6a"
        size={32}
        value={field.value ?? ""}
        onChange={handleTextChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
      />
    ),
    [handleTextChange]
  )

  const renderTextFieldHighMacAddress = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      { lowMacAddress: string; highMacAddress: string },
      "highMacAddress"
    >) => (
      <MUITextField
        id="highMacAddress"
        name={field.name}
        label="High MAC Address"
        placeholder="1a:2b:3c:4d:5e:6f"
        size={32}
        value={field.value ?? ""}
        onChange={handleTextChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
      />
    ),
    [handleTextChange]
  )

  return (
    <MUIDialog
      id="macaddress-dialog"
      variant="information"
      open={elaborateMacAddress}
      onClose={handleClose}
      heading="Please write the interval of MAC addresses that you would like to create"
      content={
        <div style={{ padding: "10px" }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "16px",
                marginBottom: "10px",
              }}
            >
              <Controller
                name="lowMacAddress"
                control={control}
                render={renderTextFieldLowMacAddress}
              />
              <Controller
                name="highMacAddress"
                control={control}
                render={renderTextFieldHighMacAddress}
              />
            </div>
          </form>
        </div>
      }
      primaryButtonText="Create"
      onClickPrimaryButton={handleSubmit(onSubmit)}
      isPrimaryButtonDisabled={isSubmitting}
      primaryButtonConfig={{
        isLoading: isSubmitting,
        type: "primary",
        size: 32,
        id: "create-mac-btn",
      }}
    />
  )
}

export function MacAddress() {
  const [elaborateMacAddress, setElaborateMacAddress] = useState(false)
  const { macAddressList, reloadMacAddresses, isLoadingMacAddress } = useMacAddresses()

  const cols = [
    { field: "value", headerName: "MAC Address" },
    {
      field: "isReserved",
      headerName: "Is Reserved",
      renderCell: (row: EntityRow) => row.isReserved ? getDoneIcon() : getEmptyIcon(),
      sortComparator: booleanComparator,
    },
    {
      field: "isUsed",
      headerName: "Is Used",
      renderCell: (row: EntityRow) => row.isUsed ? getDoneIcon() : getEmptyIcon(),
      sortComparator: booleanComparator,
    },
    {
      field: "creationDate",
      headerName: "Creation Date",
      sortComparator: dateStringComparator,
    },
  ]

  const rows = macAddressList.map((macAddress: MacAddressGet) => ({
    id: macAddress.id,
    value: macAddress.value,
    creationDate: getNiceFormattedDate(macAddress.creationDate),
    isReserved: macAddress.isReserved,
    isUsed: macAddress.isUsed,
  }))

  const setElaborateMacAddressTrue = useCallback(() => {
    setElaborateMacAddress(true)
  }, [])

  return (
    <>
      <CreateMacAddressDialog
        elaborateMacAddress={elaborateMacAddress}
        reloadMacAddresses={reloadMacAddresses}
        setElaborateMacAddress={setElaborateMacAddress}
      />

      <EntityTab
        rows={rows}
        cols={cols}
        isLoading={isLoadingMacAddress}
        toolbar={
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              width: "100%",
            }}
          >
            <MUIButton
              variant="primary"
              onClickButton={setElaborateMacAddressTrue}
              label="Create MAC Addresses"
              size={32}
            />
          </div>
        }
      />
    </>
  )
}
